import type {
  ApiList,
  BillFilters,
  DecalBillDetail,
  DecalBillSummary,
  DecalSignal,
  SignalFilters,
  KnowledgeEntry,
  WatchKeyword,
} from "./types";

// ─── Base fetch ───────────────────────────────────────────────────────────────

const API_BASE = "https://legisignal.balaram.workers.dev";

async function apiFetch<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  const url = new URL(path, API_BASE);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const res = await fetch(url.toString(), {
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }

  return res.json() as Promise<T>;
}

// ─── Signals ──────────────────────────────────────────────────────────────────

export async function fetchDecalSignals(
  filters: SignalFilters = {}
): Promise<ApiList<DecalSignal>> {
  const { signal_type, risk_level, domain, date, min_score, stage, page = 1, limit = 25 } = filters;
  return apiFetch<ApiList<DecalSignal>>("/api/decal/signals", {
    signal_type,
    risk_level,
    domain,
    date,
    min_score,
    stage,
    page,
    limit,
  });
}

export async function fetchDecalSignalById(id: number): Promise<DecalSignal> {
  return apiFetch<DecalSignal>(`/api/decal/signals/${id}`);
}

// ─── Bills ────────────────────────────────────────────────────────────────────

export async function fetchDecalBills(
  filters: BillFilters = {}
): Promise<ApiList<DecalBillSummary>> {
  const { search, domain, risk_level, min_score, page = 1, limit = 25 } = filters;
  return apiFetch<ApiList<DecalBillSummary>>("/api/decal/bills", {
    search,
    domain,
    risk_level,
    min_score,
    page,
    limit,
  });
}

export async function fetchDecalBillById(billId: number): Promise<DecalBillDetail> {
  return apiFetch<DecalBillDetail>(`/api/decal/bills/${billId}`);
}

export async function trackBill(billId: number, note?: string): Promise<void> {
  const url = new URL(`/api/decal/bills/${billId}/track`, API_BASE);
  await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note: note ?? null }),
  });
}

export async function untrackBill(billId: number): Promise<void> {
  const url = new URL(`/api/decal/bills/${billId}/track`, API_BASE);
  await fetch(url.toString(), { method: "DELETE" });
}

// ─── Knowledge base ────────────────────────────────────────────────────────────

export async function fetchKnowledge(): Promise<KnowledgeEntry[]> {
  return apiFetch<KnowledgeEntry[]>("/api/admin/knowledge");
}

export async function saveKnowledge(domain: string, label: string, context: string): Promise<void> {
  const url = new URL(`/api/admin/knowledge/${encodeURIComponent(domain)}`, API_BASE);
  await fetch(url.toString(), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label, context }),
  });
}

export async function fetchKeywords(): Promise<WatchKeyword[]> {
  return apiFetch<WatchKeyword[]>("/api/admin/keywords");
}

export async function addKeyword(keyword: string, note?: string): Promise<WatchKeyword> {
  const url = new URL("/api/admin/keywords", API_BASE);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ keyword, note: note ?? null }),
  });
  return res.json() as Promise<WatchKeyword>;
}

export async function deleteKeyword(id: number): Promise<void> {
  const url = new URL(`/api/admin/keywords/${id}`, API_BASE);
  await fetch(url.toString(), { method: "DELETE" });
}

// ─── Admin ─────────────────────────────────────────────────────────────────────

export type RefreshResult =
  | { ok: true; message: string }
  | { ok: false; quotaExceeded: boolean; message: string };

export async function triggerRefresh(): Promise<RefreshResult> {
  const url = new URL("/api/admin/refresh", API_BASE);
  const res = await fetch(url.toString(), { method: "POST" });

  if (res.status === 429) {
    return { ok: false, quotaExceeded: true, message: "quota_exceeded" };
  }
  if (!res.ok) {
    return { ok: false, quotaExceeded: false, message: `Error ${res.status}` };
  }
  const data = await res.json() as { message: string };
  return { ok: true, message: data.message };
}
